import React, { useState } from 'react';
import { Activity, History, Scissors, LogOut, Calendar, Settings } from 'lucide-react';
import { MobileBottomNav } from '../../ui/MobileBottomNav';
import { BookingItem } from './BookingItem';
import { cn } from '../../../lib/utils';
import { UserProfile } from '../../../types';

interface CustomerDashboardProps {
  user: UserProfile;
  bookings: any[];
  onCancelBooking: (id: string) => void;
  onChat: (id: string) => void;
  onEditProfile: () => void;
  unreadCount: number;
  msgUnreadMap: Record<string, number>;
  onLogOut: () => void;
  onHome: () => void;
}

export const CustomerDashboard = ({ user, bookings, onCancelBooking, onChat, onEditProfile, unreadCount, msgUnreadMap, onLogOut, onHome }: CustomerDashboardProps) => {
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');

  const filteredBookings = bookings.filter((b: any) => 
    activeTab === 'active' ? (b.status === 'Menunggu' || b.status === 'Proses') : b.status === 'Selesai'
  );

  const dashboardTabs = [
    { id: 'active', label: 'Aktif', icon: Activity, badge: unreadCount },
    { id: 'history', label: 'Riwayat', icon: History },
  ];

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-slate-50/50 pb-20 md:pb-0">
      <MobileBottomNav tabs={dashboardTabs} activeTab={activeTab} onChange={setActiveTab} user={user} />
      
      <aside className="hidden md:flex w-72 bg-white border-r border-slate-100 flex-col h-screen sticky top-0 z-50">
        <div className="p-6 border-b border-slate-50">
          <div className="flex items-center gap-4 mb-8 cursor-pointer group" onClick={onHome}>
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white shadow-lg rotate-3 group-hover:rotate-12 transition-transform">
              <Scissors className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-900 tracking-tighter leading-none group-hover:text-emerald-500 transition-colors">CukurJe</h1>
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5 group-hover:text-slate-600 transition-colors">Kembali ke Beranda</p>
            </div>
          </div>

          <nav className="space-y-1">
            {dashboardTabs.map((tab: any) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={cn(
                  "w-full flex items-center justify-between px-5 py-3 rounded-xl transition-all group",
                  activeTab === tab.id ? "bg-slate-900 text-white shadow-lg" : "text-slate-400 hover:bg-slate-50"
                )}
              >
                <div className="flex items-center gap-3">
                  <tab.icon className="w-4 h-4" />
                  <span className="text-[10px] font-black uppercase tracking-widest">{tab.label}</span>
                </div>
                {tab.badge > 0 && (
                  <span className={cn(
                    "w-5 h-5 rounded-lg flex items-center justify-center text-[10px] font-black",
                    activeTab === tab.id ? "bg-emerald-500 text-white" : "bg-red-500 text-white"
                  )}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6 mt-auto border-t border-slate-50 space-y-4">
          <button 
            onClick={onEditProfile}
            className="w-full flex items-center gap-3 px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 transition-all border border-slate-100"
          >
            <Settings className="w-4 h-4" />
            Pengaturan
          </button>
          <div className="flex items-center gap-3 px-2">
            <img src={user.avatar} className="w-8 h-8 rounded-xl object-cover shadow-sm" alt="" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-black text-slate-900 truncate tracking-tight">{user.name}</p>
              <button onClick={onLogOut} className="text-[9px] text-red-500 font-bold uppercase hover:underline">Keluar</button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Top Header */}
      <div className="md:hidden px-6 py-6 border-b border-slate-100 bg-white sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3" onClick={onHome}>
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white shadow-lg">
              <Scissors className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-900 tracking-tighter leading-none">CukurJe</h1>
              <p className="text-[9px] text-blue-500 font-black uppercase tracking-widest mt-1">Prime Club</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onEditProfile} className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400">
              <Settings className="w-5 h-5" />
            </button>
            <button onClick={onLogOut} className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400">
              <LogOut className="w-5 h-5" />
            </button>
            <img src={user.avatar} className="w-10 h-10 rounded-xl object-cover shadow-sm border border-slate-100" alt="" />
          </div>
        </div>
      </div>

      <main className="flex-1 px-4 md:px-8 py-6 md:py-8 overflow-y-auto">
        <header className="mb-6 md:mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="px-2">
            <h2 className="text-2xl font-black text-slate-900 tracking-tight leading-none mb-1">Dashboard Member</h2>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Halo, <span className="text-slate-900">{user.name.split(' ')[0]}</span></p>
            </div>
          </div>
          <button 
            onClick={onHome}
            className="flex items-center justify-center gap-2 px-6 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-slate-900/10 active:scale-95 transition-all"
          >
            Pesan Lagi
          </button>
        </header>


        <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/20 border border-slate-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
            <div className="flex items-center gap-3">
              <div className={cn("w-2 h-2 rounded-full", activeTab === 'active' ? "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]" : "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]")} />
              <h2 className="text-sm font-black text-slate-900 tracking-tight uppercase">
                {activeTab === 'active' ? 'Pesanan Aktif' : 'Riwayat Layanan'}
              </h2>
            </div>
            <div className="flex bg-white p-1 rounded-xl border border-slate-100">
              {dashboardTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={cn(
                    "px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-tight transition-all",
                    activeTab === tab.id ? "bg-slate-900 text-white" : "text-slate-400 hover:text-slate-600"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div className="p-4 md:p-6">
            <div className="space-y-3">
              {filteredBookings.length === 0 ? (
                <div className="py-16 text-center">
                  <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-6 h-6 text-slate-200" />
                  </div>
                  <p className="text-slate-900 font-black text-base mb-1">Kosong</p>
                  <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest">{activeTab === 'active' ? 'Belum ada antrean aktif' : 'Belum ada riwayat'}</p>
                </div>
              ) : (
                filteredBookings.map((b: any, index: number) => (
                  <BookingItem 
                    key={b.id} 
                    b={b} 
                    index={index} 
                    onCancelBooking={onCancelBooking} 
                    onChat={onChat} 
                    unreadCount={msgUnreadMap[b.id] || 0}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
